import { Ref } from 'vue';
import {
  HekateDisplayForm,
  FieldHtmlNameUtil,
  FieldHtmlNameType,
} from 'www-shared';
import VwoUtil from '../../../util/VwoUtil';
import PersistedProgressiveProfileData from './PersistedProgressiveProfileData';
import ProgressiveProfileDefinition from './ProgressiveProfileDefinition';
import ProgressiveProfileDefinitionProvider from './ProgressiveProfileDefinitionProvider';
import { ProgressRule } from './ProgressRule';
import { FormSubmitResolution } from '../../../../model/HekateFormSubmission';

/** We don't store the value but we do store the fact that value is in Eloqua */
type ProgressRuleKnownValue = {
  htmlName: FieldHtmlNameType;
}

/** For non Personally Identifiable Information (PII) store the fieldValue. (Typically the branch) */
type ProgressRuleBranchValue = {
  htmlName: FieldHtmlNameType;
  fieldValue: string | number | boolean;
}

export default class ProgressiveProfiler {
  private static readonly localStorageKey = 'www-HekateProgressiveProfiles';

  /** Hide/Show fields in the form depending on the user's Progressive Profile Data */
  public static initDisplay({
    formDefinition,
    formNotValidErrorMsg,
  }: {
    formDefinition: Ref<HekateDisplayForm | null>,
    formNotValidErrorMsg: Ref<string>,
  }): void {
    if (formDefinition.value === null) {
      return;
    }

    const progressiveProfileDefinition = ProgressiveProfileDefinitionProvider.getDefinition({ formDefinition: formDefinition.value });
    if (!progressiveProfileDefinition || !formDefinition.value.elements.find((elm) => progressiveProfileDefinition.getBranchField() === elm.htmlName)) {
      return;
    }

    const missingFields = progressiveProfileDefinition.isAllFormRequiredFieldsPresent(formDefinition.value.displayFields);
    if (missingFields.length > 0) {
      // eslint-disable-next-line no-console
      console.error(`Missing form fields ${missingFields.join(',')} in form ${formDefinition.value.id}`);
    }

    // null check above as we can safely cast formDefinition
    progressiveProfileDefinition.realtimeBranchModifications({
      formDefinition: formDefinition as Ref<HekateDisplayForm>,
      formNotValidErrorMsg,
    });

    const profileData = ProgressiveProfiler.getProfileData();

    // Pre-populate fields if we know their value
    formDefinition.value.displayFields.forEach((field) => {
      if (field.isIgnorePrefillAndEventBusValues) {
        return;
      }

      const preFilled = progressiveProfileDefinition.getPreFilledFields().find((htmlName) => htmlName === field.htmlName);
      if (!preFilled) {
        return;
      }
      const found = Object.entries(profileData.preFilledFieldValues)
        .filter(([htmlName]) => htmlName === field.htmlName && field.componentName !== 'hidden-field')
        .map(([, vv]) => vv);
      if (found.length !== 1) {
        return;
      }

      // eslint-disable-next-line prefer-destructuring
      field.val = found[0];
    });

    // There is no rules so we are only pre-populating
    if (progressiveProfileDefinition.getProgressRules().length === 0) {
      return;
    }

    const currentRule: ProgressRule | null = ProgressiveProfiler.findMatchedRule({
      progressiveProfileDefinition,
      profileData,
    });

    // Hide/show fields based on current rule
    formDefinition.value.displayFields.forEach((field) => {
      if (field.displayType === 'recaptcha') {
        return;
      }

      const alwaysShow = progressiveProfileDefinition.getAlwaysShownFields().find((htmlName) => htmlName === field.htmlName);
      if (alwaysShow) {
        return;
      }

      if (!currentRule) {
        field.componentName = 'hidden-field';
        return;
      }

      const isShown = currentRule.fieldToShow.find((htmlName) => htmlName === field.htmlName);
      if (isShown) {
        field.isRequired = true;
      } else {
        field.componentName = 'hidden-field';
      }
    });
  }

  /** Called on successful submit */
  public static onSuccess({
    formDefinition,
    redirectUrl,
    formSubmitResolution,
  }: {
    formDefinition: Ref<HekateDisplayForm | null>,
    redirectUrl: string | null,
    formSubmitResolution?: Promise<FormSubmitResolution | null>,
  }): void {
    if (formDefinition.value === null) {
      return;
    }
    const progressiveProfileDefinition = ProgressiveProfiler.getProgressiveProfileDefinition({ formDefinition });
    if (!progressiveProfileDefinition) {
      return;
    }

    // send VWO Custom conversion for split tests
    VwoUtil.vwoSubmitFormConversionPush({ formId: formDefinition.value.id });

    // check null above = safe cast
    progressiveProfileDefinition.onSuccess({
      formDefinition: formDefinition as Ref<HekateDisplayForm>,
      redirectUrl,
      formSubmitResolution: formSubmitResolution || Promise.resolve(null),
    });
  }

  /** Run when eloqua is down (i.e. the user has seen two eloqua 500 errors) */
  public static onEloquaDown({
    formDefinition,
    redirectUrl,
  }: {
    formDefinition: Ref<HekateDisplayForm | null>,
    redirectUrl: string | null,
  }): void {
    const progressiveProfileDefinition = ProgressiveProfiler.getProgressiveProfileDefinition({ formDefinition });
    if (!progressiveProfileDefinition) {
      return;
    }

    // check null above = safe cast
    progressiveProfileDefinition.onEloquaDown({ formDefinition: formDefinition as Ref<HekateDisplayForm>, redirectUrl });
  }

  /** Get the ProgressiveProfileDefinition for the current for or null if non */
  private static getProgressiveProfileDefinition({
    formDefinition,
  }: {
    formDefinition: Ref<HekateDisplayForm | null>,
  }): null | ProgressiveProfileDefinition {
    if (formDefinition.value === null) {
      return null;
    }

    const progressiveProfileDefinition = ProgressiveProfileDefinitionProvider.getDefinition({ formDefinition: formDefinition.value });
    if (!progressiveProfileDefinition || !formDefinition.value.elements.find((elm) => progressiveProfileDefinition.getBranchField() === elm.htmlName)) {
      return null;
    }
    return progressiveProfileDefinition;
  }

  /** Store values that are needed for profiling in `localStorage` */
  public static persistAnswers({
    formDefinition,
  }: {
    formDefinition: HekateDisplayForm,
  }): void {
    if (formDefinition === null) {
      return;
    }

    const progressiveProfileDefinition = ProgressiveProfileDefinitionProvider.getDefinition({ formDefinition });
    if (!progressiveProfileDefinition || !formDefinition.elements.find((elm) => progressiveProfileDefinition.getBranchField() === elm.htmlName)) {
      return;
    }

    const knownFieldValues: ProgressRuleKnownValue[] = [];
    const branchFieldValues: ProgressRuleBranchValue[] = [];
    // For each of the progressive rules store the fields that are inspected by
    // any given rule if the value is not empty store that in branch fields and/or known fields
    progressiveProfileDefinition.getProgressRules().forEach((rule) => {
      rule.fieldToShow.forEach((htmlName) => {
        const foundField = formDefinition.displayFields.find((field) => field.htmlName === htmlName && field.val);
        if (!foundField) {
          return;
        }
        if (progressiveProfileDefinition.getBranchField() === htmlName) {
          branchFieldValues.push({
            htmlName,
            fieldValue: foundField.val,
          });
        }

        knownFieldValues.push({
          htmlName,
        });
      });
    });

    const preFilledFields: ProgressRuleBranchValue[] = [];
    formDefinition.displayFields.forEach((field) => {
      if (progressiveProfileDefinition.getPreFilledFields().indexOf(field.htmlName as FieldHtmlNameType) === -1) {
        return;
      }
      preFilledFields.push({
        htmlName: field.htmlName as FieldHtmlNameType,
        fieldValue: field.val,
      });
    });

    const profileData = ProgressiveProfiler.getProfileData();

    // Persist the list of fields that are thought to be 'known' (not what the value is, just that it has been set)
    knownFieldValues.forEach((toPersist) => {
      profileData.knownFieldValues[toPersist.htmlName] = true;
    });

    // Persist the values that are needed for branching
    branchFieldValues.forEach((toPersist) => {
      profileData.branchFieldValues[toPersist.htmlName] = toPersist.fieldValue;
    });

    // Persist the values that will be pre-filled
    preFilledFields.forEach((toPersist) => {
      profileData.preFilledFieldValues[toPersist.htmlName] = toPersist.fieldValue;
    });

    ProgressiveProfiler.setProfiledData(new PersistedProgressiveProfileData(profileData));
  }

  /** Find the current matched rule or null if there are no more fields to parse */
  private static findMatchedRule({
    profileData,
    progressiveProfileDefinition,
  }: {
    profileData: PersistedProgressiveProfileData
    progressiveProfileDefinition: ProgressiveProfileDefinition,
  }): ProgressRule | null {
    if (!profileData) {
      return progressiveProfileDefinition.getProgressRules()[0];
    }
    if (profileData.branchFieldValues.length === 0) {
      return progressiveProfileDefinition.getProgressRules()[0];
    }
    const usersBranch = profileData.branchFieldValues[FieldHtmlNameUtil.chooseOnshapeEvaluatingStatus] || '';

    const curRule = progressiveProfileDefinition.getProgressRules().filter((rule) => rule.branch === usersBranch)
      .find((rule) => {
        for (let ii = 0; ii < rule.fieldToShow.length; ii += 1) {
          const toShow = rule.fieldToShow[ii];
          const ruleValues = profileData.knownFieldValues[toShow];
          if (!ruleValues) {
            return true;
          }
        }
        return false;
      });

    return curRule || null;
  }

  /** In the future we could push this data to the network and return a promise */
  private static setProfiledData(profileData: PersistedProgressiveProfileData) {
    if (window.oneTrustHelper && !window.oneTrustHelper.isConsent(['F'])) {
      return;
    }

    try {
      profileData.lastModified = Date.now();
      localStorage.setItem(ProgressiveProfiler.localStorageKey, JSON.stringify(profileData));
    } catch (e) {
      // ignore localStorage is disabled
    }
  }

  /** In the future we could pull this from the network and return a promise */
  private static getProfileData(): PersistedProgressiveProfileData {
    let profileData = new PersistedProgressiveProfileData({
      branchFieldValues: {},
      knownFieldValues: {},
      preFilledFieldValues: {},
      lastModified: Date.now(),
    });

    try {
      // TODO: debug code, can be removed
      if (`${window.location.search}`.indexOf('clearProgressiveProfile=true') !== -1) {
        ProgressiveProfiler.setProfiledData(profileData);
        window.location.search = window.location.search.replace('clearProgressiveProfile=true', '');
        return profileData;
      }
      const json = localStorage.getItem(ProgressiveProfiler.localStorageKey);
      if (json) {
        // TODO make sure this actually is an instance
        profileData = new PersistedProgressiveProfileData(JSON.parse(json));
      }
    } catch (e) {
      // ignore localStorage is disabled
    }

    return profileData;
  }
}
